/**
 * Boss definitions - 8 boss enemies that appear as timed events
 * Headless - no GUI
 */

import type { BossId } from '../state.js';

export type BossRewardType = 'ip_burst' | 'docs_bonus' | 'cps_boost' | 'gc_bonus';

export type BossUnlockCondition =
  | { type: 'start' }
  | { type: 'prestige'; count: number }
  | { type: 'conquered'; countryId: string };

export interface BossDef {
  id: BossId;
  nameKey: string;
  descKey: string;
  imagePath: string;
  phase2ImagePath: string;
  baseHpMultiplier: number; // boss HP = CPS * this value
  timeLimit: number;        // time limit in ms
  rewardType: BossRewardType;
  unlockCondition: BossUnlockCondition;
}

export interface BossReward {
  ip: number;
  docs: number;
  gc: number;
  boostSeconds: number;
}

export const BOSS_DEFS: BossDef[] = [
  {
    id: 'resistance_captain',
    nameKey: 'boss_resistance_captain',
    descKey: 'boss_resistance_captain_desc',
    imagePath: 'images/boss-resistance-captain.png',
    phase2ImagePath: 'images/boss-resistance-captain-rage.png',
    baseHpMultiplier: 15,
    timeLimit: 30_000,
    rewardType: 'ip_burst',
    unlockCondition: { type: 'start' },
  },
  {
    id: 'cia_agent',
    nameKey: 'boss_cia_agent',
    descKey: 'boss_cia_agent_desc',
    imagePath: 'images/boss-cia-agent.png',
    phase2ImagePath: 'images/boss-cia-agent-rage.png',
    baseHpMultiplier: 20,
    timeLimit: 30_000,
    rewardType: 'docs_bonus',
    unlockCondition: { type: 'start' },
  },
  {
    id: 'mib_investigator',
    nameKey: 'boss_mib_investigator',
    descKey: 'boss_mib_investigator_desc',
    imagePath: 'images/boss-mib.png',
    phase2ImagePath: 'images/boss-mib-rage.png',
    baseHpMultiplier: 25,
    timeLimit: 35_000,
    rewardType: 'cps_boost',
    unlockCondition: { type: 'start' },
  },
  {
    id: 'un_inspector',
    nameKey: 'boss_un_inspector',
    descKey: 'boss_un_inspector_desc',
    imagePath: 'images/boss-un-inspector.png',
    phase2ImagePath: 'images/boss-un-inspector-rage.png',
    baseHpMultiplier: 30,
    timeLimit: 40_000,
    rewardType: 'ip_burst',
    unlockCondition: { type: 'conquered', countryId: 'usa' },
  },
  {
    id: 'whistleblower',
    nameKey: 'boss_whistleblower',
    descKey: 'boss_whistleblower_desc',
    imagePath: 'images/boss-whistleblower.png',
    phase2ImagePath: 'images/boss-whistleblower-rage.png',
    baseHpMultiplier: 35,
    timeLimit: 40_000,
    rewardType: 'docs_bonus',
    unlockCondition: { type: 'conquered', countryId: 'usa' },
  },
  {
    id: 'journalist',
    nameKey: 'boss_journalist',
    descKey: 'boss_journalist_desc',
    imagePath: 'images/boss-journalist.png',
    phase2ImagePath: 'images/boss-journalist-rage.png',
    baseHpMultiplier: 50,
    timeLimit: 45_000,
    rewardType: 'cps_boost',
    unlockCondition: { type: 'prestige', count: 1 },
  },
  {
    id: 'truth_seeker',
    nameKey: 'boss_truth_seeker',
    descKey: 'boss_truth_seeker_desc',
    imagePath: 'images/boss-truth-seeker.png',
    phase2ImagePath: 'images/boss-truth-seeker-rage.png',
    baseHpMultiplier: 75,
    timeLimit: 45_000,
    rewardType: 'gc_bonus',
    unlockCondition: { type: 'prestige', count: 1 },
  },
  {
    id: 'galactic_guardian',
    nameKey: 'boss_galactic_guardian',
    descKey: 'boss_galactic_guardian_desc',
    imagePath: 'images/boss-galactic-guardian.png',
    phase2ImagePath: 'images/boss-galactic-guardian-rage.png',
    baseHpMultiplier: 120,
    timeLimit: 50_000,
    rewardType: 'gc_bonus',
    unlockCondition: { type: 'prestige', count: 2 },
  },
];

/** Get boss definition by ID */
export function getBossDef(id: BossId): BossDef | undefined {
  return BOSS_DEFS.find(b => b.id === id);
}
